import { format, isSameDay, subDays } from 'date-fns'

import type { PendingMessage } from './optimisticMessages'
import type { SupportMessage, TicketMessageAuthorType } from './types'

// Replies that land close together read as one turn in the conversation, so
// they share a single identity header.
const GROUP_WINDOW_MS = 5 * 60 * 1000

// Opening an old thread should not ask the service to unfurl every link it ever
// held, so only the newest replies carry a preview card.
const LINK_PREVIEW_WINDOW = 10

export type ThreadEntry =
  | {
      kind: 'message'
      key: string
      message: SupportMessage
      showsLinkPreview: boolean
    }
  | { kind: 'pending'; key: string; pending: PendingMessage }

export interface ThreadGroupRow {
  kind: 'group'
  key: string
  authorType: 'user' | 'staff'
  staffName: string | null
  startedAt: string
  entries: ThreadEntry[]
}

export type ThreadRow =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'unread'; key: string }
  | { kind: 'status'; key: string; message: SupportMessage }
  | ThreadGroupRow

interface ThreadPost {
  entry: ThreadEntry
  createdAt: string
  authorType: TicketMessageAuthorType
  staffName: string | null
}

// What counted as unread the moment the thread opened: everything the user had
// not read yet (`since`) that the service already held (`until`). A reply that
// lands while the user is watching falls outside the window, so the marker
// never points at something they saw arrive.
export interface UnreadEntryWindow {
  since: string | null
  until: string | null
}

// The list row that opens a thread can lag the service, since a stream that was
// down never bumped it. Its timestamp alone would leave every reply the user
// missed above the window and show no marker at all. Everything the first load
// returned was already on the service when the thread opened, so it belongs
// inside the window.
export const withLoadedMessages = (
  entryWindow: UnreadEntryWindow,
  messages: SupportMessage[],
): UnreadEntryWindow => {
  if (entryWindow.until === null) return entryWindow

  const newestLoadedAt = messages.reduce(
    (newest, message) =>
      message.createdAt > newest ? message.createdAt : newest,
    entryWindow.until,
  )
  return { ...entryWindow, until: newestLoadedAt }
}

interface ThreadTimelineInput {
  messages: SupportMessage[]
  pendingMessages: PendingMessage[]
  unreadWindow: UnreadEntryWindow
  linkPreviewSince: string | null
  now: Date
}

export const dayLabel = (instant: Date, now: Date) => {
  if (isSameDay(instant, now)) return 'Today'
  if (isSameDay(instant, subDays(now, 1))) return 'Yesterday'
  return format(instant, 'MMM d, yyyy')
}

export const messageTime = (createdAt: string) =>
  format(new Date(createdAt), 'h:mm a')

// The calendar day in the browser's zone, so two separators can never collide
// on one key the way a UTC date can either side of a local midnight.
const dayKey = (instant: Date) => format(instant, 'yyyy-MM-dd')

// Pending replies carry the browser's clock while messages carry the service's,
// so the gap between them can read as negative when the two disagree.
const withinGroupWindow = (earlier: ThreadPost, later: ThreadPost) =>
  Math.abs(
    new Date(later.createdAt).getTime() - new Date(earlier.createdAt).getTime(),
  ) <= GROUP_WINDOW_MS

// Pending replies are appended by the store, not merged by time, so a failed
// one left on screen can be older than the newest message. Sorting keeps the
// transcript reading forwards and keeps every day separator on its own key.
// Comparing the strings is time order only while every stamp is UTC, which
// holds: the service serialises UTC and a pending uses toISOString().
const byOldestFirst = (first: ThreadPost, second: ThreadPost) =>
  first.createdAt.localeCompare(second.createdAt)

// The oldest message allowed a preview card, or null when the thread is short
// enough that every message is allowed one. Status lines carry no link, so they
// do not use up the allowance.
export const linkPreviewCutoff = (
  messages: SupportMessage[],
): string | null => {
  const replies = messages.filter((message) => message.authorType !== 'system')
  if (replies.length <= LINK_PREVIEW_WINDOW) return null

  return replies[replies.length - LINK_PREVIEW_WINDOW].createdAt
}

// A cut-off, not a sliding window: a message keeps its card once it has one, and
// a reply that lands later is newer than the cut-off so it gets one too.
const previewsLink = (message: SupportMessage, since: string | null) =>
  since === null || message.createdAt >= since

const toThreadPosts = (
  messages: SupportMessage[],
  pendingMessages: PendingMessage[],
  linkPreviewSince: string | null,
): ThreadPost[] =>
  [
    ...messages.map(
      (message): ThreadPost => ({
        entry: {
          kind: 'message',
          key: `message-${message.id}`,
          message,
          showsLinkPreview: previewsLink(message, linkPreviewSince),
        },
        createdAt: message.createdAt,
        authorType: message.authorType,
        staffName: message.staffName,
      }),
    ),
    ...pendingMessages.map(
      (pending): ThreadPost => ({
        entry: { kind: 'pending', key: `pending-${pending.id}`, pending },
        createdAt: pending.createdAt,
        authorType: 'user',
        staffName: null,
      }),
    ),
  ].sort(byOldestFirst)

const opensUnreadRun = (post: ThreadPost, entryWindow: UnreadEntryWindow) =>
  post.authorType === 'staff' &&
  entryWindow.since !== null &&
  post.createdAt > entryWindow.since &&
  (entryWindow.until === null || post.createdAt <= entryWindow.until)

// Folds the thread into the rows the transcript renders: a day separator at
// every date change, one marker above the first reply the user has not read,
// status lines on their own, and everything else in author groups.
export const buildThreadRows = ({
  messages,
  pendingMessages,
  unreadWindow,
  linkPreviewSince,
  now,
}: ThreadTimelineInput): ThreadRow[] => {
  const rows: ThreadRow[] = []
  let group: ThreadGroupRow | null = null
  let previous: ThreadPost | null = null
  let hasUnreadMarker = false

  for (const post of toThreadPosts(
    messages,
    pendingMessages,
    linkPreviewSince,
  )) {
    const instant = new Date(post.createdAt)

    if (!previous || !isSameDay(instant, new Date(previous.createdAt))) {
      rows.push({
        kind: 'day',
        key: `day-${dayKey(instant)}`,
        label: dayLabel(instant, now),
      })
      group = null
    }

    if (!hasUnreadMarker && opensUnreadRun(post, unreadWindow)) {
      rows.push({ kind: 'unread', key: `unread-${post.entry.key}` })
      hasUnreadMarker = true
      group = null
    }

    if (post.authorType === 'system' && post.entry.kind === 'message') {
      rows.push({
        kind: 'status',
        key: post.entry.key,
        message: post.entry.message,
      })
      group = null
      previous = post
      continue
    }

    if (
      group &&
      previous &&
      group.authorType === post.authorType &&
      group.staffName === post.staffName &&
      withinGroupWindow(previous, post)
    ) {
      group.entries.push(post.entry)
    } else {
      group = {
        kind: 'group',
        key: `group-${post.entry.key}`,
        authorType: post.authorType === 'staff' ? 'staff' : 'user',
        staffName: post.staffName,
        startedAt: post.createdAt,
        entries: [post.entry],
      }
      rows.push(group)
    }

    previous = post
  }

  return rows
}
